<!-- GeneralSpreadSelectorV3.vue -->
<template>
  <div v-if="hasSpecifier(getEventMarket(event, marketDef))" class="outcome-box specifier-box-wrapper" ref="wrapper">
    <button
      class="specifier-trigger"
      :class="[getSpecifierType(getEventMarket(event, marketDef).specifier), { 'is-main-line': isMainLine }]"
      @click.stop="toggleDropdown"
    >
      <span class="trigger-label">{{ formatLineDisplay(getEventMarket(event, marketDef).specifier, marketDef) }}</span>
      <svg class="trigger-chevron" :class="{ open: showDropdown }" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="6 9 12 15 18 9"/>
      </svg>
    </button>

    <transition name="spec-drop">
      <div v-if="showDropdown" class="spec-dropdown" :style="dropdownStyle">
        <div
          v-for="(line, lIdx) in getAlternativeLines(event, getEventMarket(event, marketDef))"
          :key="lIdx"
          class="spec-row"
          :class="{ active: isActive(line.specifier) }"
          @mousedown.prevent="handleChange(line.specifier)"
        >
          <span class="spec-badge" :class="getSpecifierType(line.specifier)">
            {{ formatLineDisplay(line.specifier, marketDef) }}
          </span>

          <div class="spec-odds">
            <template v-for="(o, i) in line.outcome.slice(0, 3)">
              <button
                v-if="isValidOdd(o)"
                :key="i"
                class="spec-odd-btn"
                :class="{ 'odd-selected': oddsSources.includes(o.unique_id) }"
                @mousedown.stop="handlePutCart(line, o)"
              >
                <span class="spec-odd-name">{{ o.outcome_alias || o.outcome_name }}</span>
                <span class="spec-odd-val">{{ o.outcome_odd_value | odds_type_filter }}</span>
                <!-- Corner ribbon "+N%" for boosted odds inside the
                     alternative-lines dropdown. Without this the odds
                     served with an active BOOST rule looked identical to
                     non-boosted odds here, even though the price was
                     already the boosted one — the player couldn't tell
                     WHY a specific line was more attractive than the
                     others. Same badge used everywhere else. -->
                <OddBoostBadge :odd="o" size="sm" :show-percent="false"/>
              </button>
              <button v-else :key="i" class="spec-odd-btn locked spec-lock-wrap" disabled>
                <svg class="spec-lock" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </button>
            </template>
          </div>

<!--          <svg v-if="isActive(line.specifier)" class="spec-check" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">-->
<!--            <polyline points="20 6 9 17 4 12"/>-->
<!--          </svg>-->
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import OddBoostBadge from '@/components/generic/OddBoostBadge.vue';

export default {
  name: 'GeneralSpreadSelectorV3',
  components: {OddBoostBadge},
  props: {
    event: { type: Object, required: true },
    marketDef: { type: Object, required: true },
    getEventMarket: { type: Function, required: true },
    hasSpecifier: { type: Function, required: true },
    getSpecifierType: { type: Function, required: true },
    formatLineDisplay: { type: Function, required: true },
    getAlternativeLines: { type: Function, required: true },
    changeLine: { type: Function, required: true },
    isValidOdd: { type: Function, required: true },
    oddsSources: { type: Array, default: () => [] },
  },
  data() {
    return { showDropdown: false, wrapperRect: null };
  },
  computed: {
    isMainLine() {
      const mkt = this.getEventMarket(this.event, this.marketDef);
      return mkt && String(mkt.specifier) === String(this.marketDef.mainLine);
    },
    dropdownStyle() {
      if (!this.wrapperRect) return { position: 'fixed', top: '0px', left: '0px' };
      const dropdownWidth = 240;
      const dropdownHeight = 260;
      const r = this.wrapperRect;
      const spaceBelow = window.innerHeight - r.bottom;
      const goUp = spaceBelow < dropdownHeight && r.top > dropdownHeight;
      const top = goUp ? (r.top - dropdownHeight) + 'px' : (r.bottom + 4) + 'px';
      const rawLeft = r.left;
      const left = (rawLeft + dropdownWidth > window.innerWidth)
        ? (window.innerWidth - dropdownWidth - 4) + 'px'
        : rawLeft + 'px';
      return { position: 'fixed', top, left, bottom: 'auto' };
    },
  },
  mounted() {
    this._closeOnOutside = (e) => {
      if (!this.$refs.wrapper || !this.$refs.wrapper.contains(e.target)) {
        this.showDropdown = false;
      }
    };
    document.addEventListener('mousedown', this._closeOnOutside);
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this._closeOnOutside);
  },
  methods: {
    toggleDropdown() {
      if (!this.showDropdown) {
        this.wrapperRect = this.$refs.wrapper.getBoundingClientRect();
      }
      this.showDropdown = !this.showDropdown;
    },
    handleChange(specifier) {
      this.changeLine(this.event, this.marketDef, specifier);
      this.showDropdown = false;
    },
    handlePutCart(market, odd) {
      this.$emit('put-cart', { market, odd });
      this.toggleDropdown()
    },
    isActive(specifier) {
      const mkt = this.getEventMarket(this.event, this.marketDef);
      return mkt && String(specifier) === String(mkt.specifier);
    }
  }
}
</script>

<style scoped>
.outcome-box.specifier-box-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── TRIGGER BUTTON ── */
.specifier-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 0 10px;
  height: 100%;
  border-radius: 4px;
  border: 1px solid var(--bg-card-hover);
  color: var(--text-platform);
  font-size: var(--font-size-xl);
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
  touch-action: manipulation;
  width: 100%;
}

/*.specifier-trigger.is-main-line { border-color:  #a37e2c; }*/

.trigger-label { flex: 1; }
.trigger-chevron {
  flex-shrink: 0;
  opacity: 0.7;
  transition: transform 0.2s cubic-bezier(.34,1.56,.64,1);
}
.trigger-chevron.open { transform: rotate(180deg); }

/* ── DROPDOWN PANEL ── */
.spec-dropdown {
  position: fixed;
  z-index: 9999;
  min-width: 240px;
  max-height: 260px;
  overflow-y: auto;
  overflow-x: hidden;
  background: var(--bg-card, #252525);
  border: 1px solid var(--border-color, #3a3a3a);
  border-radius: 4px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.55), 0 2px 8px rgba(0, 0, 0, 0.3);
  scrollbar-width: none;
}
.spec-dropdown::-webkit-scrollbar { display: none; }

/* ── DROPDOWN ROWS ── */
.spec-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  cursor: pointer;
  border-bottom: 1px solid var(--border-color, #2e2e2e);
  transition: background 0.12s;
}
.spec-row:last-child { border-bottom: none; }
.spec-row:hover     { background: rgba(255,255,255,0.04); }
.spec-row.active    { background: var(--bg-cell-hover); }

.spec-badge {
  flex-shrink: 0;
  font-size: var(--font-size-xl);
  font-weight: 800;
  padding: 2px 7px;
  border-radius: 4px;
  background: rgba(255,255,255,0.06);
  color: var(--text-platform);
  min-width: 36px;
  text-align: center;
}

/* ── ODD BUTTONS ── */
.spec-odds {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-end;
}

.spec-odd-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  min-width: 64px;
  height: 36px;
  padding: 0 6px;
  border-radius: 6px;
  border: 1px solid var(--border-color, #3a3a3a);
  background: var(--bg-body, #1a1a1a);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  touch-action: manipulation;
  /* Positioning context for the `OddBoostBadge --corner` triangle —
     the badge is absolute-positioned and needs a positioned ancestor
     to anchor to; without this it would climb the DOM tree to
     whichever the next positioned parent is (unpredictable). */
  position: relative;
}
.spec-odd-btn:hover {
  background: var(--bg-cell-hover, #2a2a2a);
  border-color: var(--color-primary-light, #4a7a5a);
}
.spec-odd-btn:active { opacity: 0.75; }
.spec-odd-btn.odd-selected {
  background: var(--color-primary-gradient);
  border-color: var(--color-primary-light-op-50);
  border-bottom-color: var(--color-primary-light);
}

.spec-odd-btn.locked {
  background: var(--bg-cell-locked);
}

.spec-odd-name {
  font-size: var(--font-size-xs);
  color: var(--text-op-50-platform, #888);
  letter-spacing: 0.3px;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 50px;
}
.spec-odd-val {
  font-size: var(--font-size-xl);
  font-weight: 700;
  color: var(--text-platform, #fff);
  line-height: 1;
}
.spec-odd-btn.odd-selected .spec-odd-name,
.spec-odd-btn.odd-selected .spec-odd-val { color: var(--text-platform, #fff); }

.spec-lock-wrap { display: flex; align-items: center; justify-content: center; width: 44px; height: 36px; }
.spec-lock { opacity: 0.3; flex-shrink: 0; }

.spec-check {
  flex-shrink: 0;
  color: var(--color-accent, #a37e2c);
}

/* ── TRANSITION ── */
.spec-drop-enter-active { transition: opacity 0.15s ease, transform 0.18s cubic-bezier(0.34, 1.4, 0.64, 1); }
.spec-drop-leave-active { transition: opacity 0.1s ease, transform 0.12s ease; }
.spec-drop-enter, .spec-drop-leave-to { opacity: 0; transform: translateY(-6px); }
.spec-dropdown.drop-up.spec-drop-enter,
.spec-dropdown.drop-up.spec-drop-leave-to { transform: translateY(6px); }
</style>
